import { h, proxyCustomElement, HTMLElement, createEvent, Host } from '@stencil/core/internal/client';
import './toast-popup.js';
import { s as showToast } from './toast-manager.js';
import { d as defineCustomElement$3 } from './input.js';
import { d as defineCustomElement$2 } from './link.js';

const ClosedEyeIcon = () => (h("svg", { fill: "none", width: "18", height: "18", viewBox: "0 0 20 20", xmlns: "http://www.w3.org/2000/svg" },
  h("g", { fill: "var(--pass-icon-color)" },
    h("path", { d: "m20.078 9.5434s-3.3898 5.8333-9.8305 5.8333c-6.4406 0-10.169-5.8333-10.169-5.8333v-0.3775s5.0728 4.1275 10.169 4.1275c5.1859 0 9.8305-4.1284 9.8305-4.1284v0.37833z" }),
    h("path", { d: "m19.831 7.2525c0-0.1075-0.0445-0.20667-0.1294-0.27167-0.6759-0.51916-3.9885-2.8142-9.7011-2.8142-5.705 0-9.1495 2.2892-9.8619 2.8117-0.090166 0.06584-0.13833 0.17-0.13833 0.2825 0 0.28917 0.31592 0.47584 0.57217 0.34084 1.4365-0.75334 5.3712-2.6017 9.428-2.6017 4.0999 0 7.8614 1.8233 9.2571 2.5867 0.2534 0.13833 0.5734-0.04584 0.5734-0.33417z" })),
  h("g", { stroke: "var(--pass-icon-color)", "stroke-linecap": "round" },
    h("path", { d: "m2.4461 12.244-1.2044 1.8067" }),
    h("path", { d: "m5.1342 13.838-0.63125 2.525" }),
    h("path", { d: "m9.4141 14.583v3.3333" }),
    h("path", { d: "m13.669 14.47 0.6313 2.525" }),
    h("path", { d: "m17.217 12.074 1.6951 1.975" }))));

const OpenEyeIcon = () => (h("svg", { width: "18", height: "18", viewBox: "0 0 20 20", fill: "var(--pass-icon-color)", xmlns: "http://www.w3.org/2000/svg" },
  h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M10.1698 16.6665C16.6101 16.6665 20 11.2498 20 11.2498C20 11.2498 16.2703 5.83313 10.1698 5.83313C4.06759 5.83313 0 11.2498 0 11.2498C0 11.2498 3.72962 16.6665 10.1698 16.6665ZM10 15.8331C12.641 15.8331 14.7826 13.7807 14.7826 11.2498C14.7826 8.71887 12.641 6.66646 10 6.66646C7.35903 6.66646 5.21739 8.71887 5.21739 11.2498C5.21739 13.7807 7.35903 15.8331 10 15.8331Z" }),
  h("path", { d: "M10 14.1665C11.6809 14.1665 13.0435 12.8606 13.0435 11.2498C13.0435 9.63897 11.6809 8.33313 10 8.33313C8.31915 8.33313 6.95654 9.63897 6.95654 11.2498C6.95654 12.8606 8.31915 14.1665 10 14.1665Z" }),
  h("path", { d: "M19.8652 6.41237C19.8652 6.30928 19.8216 6.21293 19.7376 6.14852C19.065 5.63272 15.7458 3.33337 10.0177 3.33337C4.29783 3.33337 0.846383 5.62594 0.136739 6.14622C0.0472609 6.21182 0 6.31256 0 6.42052C0 6.70969 0.332904 6.89407 0.597844 6.75569C2.05675 5.99373 5.97663 4.16648 10.0177 4.16648C14.1006 4.16648 17.8485 5.96818 19.266 6.7395C19.5277 6.88187 19.8652 6.7007 19.8652 6.41237Z" })));

function clipboardCopy(text) {
  // Use the Async Clipboard API when available. Requires a secure browsing
  // context (i.e. HTTPS)
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text).catch(function (err) {
      throw err !== undefined ? err : new DOMException('The request is not allowed', 'NotAllowedError');
    });
  }
  // ...Otherwise, use document.execCommand() fallback
  // Put the text to copy into a <span>
  const span = document.createElement('span');
  span.textContent = text;
  // Preserve consecutive spaces and newlines
  span.style.whiteSpace = 'pre';
  // Add the <span> to the page
  document.body.appendChild(span);
  // Make a selection object representing the range of text selected by the user
  const selection = window.getSelection();
  const range = window.document.createRange();
  selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
  range.selectNode(span);
  selection === null || selection === void 0 ? void 0 : selection.addRange(range);
  // Copy text to the clipboard
  let success = false;
  try {
    success = window.document.execCommand('copy');
  }
  catch (err) {
    console.error('error', err);
  }
  // Cleanup
  selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
  window.document.body.removeChild(span);
  return success ? Promise.resolve() : Promise.reject(new DOMException('The request is not allowed', 'NotAllowedError'));
}

/** перечисления типов символов */
var CharTypes;
(function (CharTypes) {
  CharTypes["NUMBER"] = "NUMBER";
  CharTypes["ALPHA"] = "ALPHA";
  CharTypes["UPPER"] = "UPPER";
  CharTypes["LOWER"] = "LOWER";
  CharTypes["SPECIAL"] = "SPECIAL";
  CharTypes["OTHER"] = "OTHER";
})(CharTypes || (CharTypes = {}));
/**
 * Класс генерации пароля
 * Генерирует пароль заданной длины из заданных символов
 */
class PasswordGenerator {
  constructor(length = 8, options = {
    useNumber: true,
    useUpperCase: true,
    useLowerCase: true,
    useSpecial: false,
  }) {
    /** длина пароля */
    this._length = 8;
    /** символы цифр */
    this._numberChars = '1234567890';
    /** символы букв в нижнем регистре */
    this._lowerCaseAlphaChars = 'abcdefghijklmnopqrstuvwxyz';
    /** символы букв в верхнем регистре */
    this._upperCaseAlphaChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    /** спец символы */
    this._specialChars = '!@#$%^&*?_~';
    /** строка с символоми используемых при генерации */
    this._chars = '';
    /** схема генерируемого пароля по типам символов */
    this._pullTypes = [];
    this._setPasswordOptions(length, options);
  }
  /**
   * Задать настройки генерации пароля
   * @param length - длина генерируемого пароля
   * @param options - объект настроек
   */
  _setPasswordOptions(length, options) {
    this._length = length;
    if (options.useNumber) {
      this._chars += this._numberChars;
    }
    if (options.useLowerCase) {
      this._chars += this._lowerCaseAlphaChars;
    }
    if (options.useUpperCase) {
      this._chars += this._upperCaseAlphaChars;
    }
    if (options.useSpecial) {
      this._chars += this._specialChars;
    }
    if (options.customChars) {
      this._chars += options.customChars;
    }
    this._charsByType = {
      NUMBER: this._getChar(new RegExp(/\d/g)),
      UPPER: this._getChar(new RegExp(/[A-Z]/g)),
      LOWER: this._getChar(new RegExp(/[a-z]/g)),
      ALPHA: this._getChar(new RegExp(/([A-Z][a-z])/g)),
      SPECIAL: this._getChar(new RegExp(/[!,@,#,$,%,^,&,*,?,_,~]/g)),
      OTHER: this._getChar(new RegExp(/[^A-Z^a-z^0-9,!,@,#,$,%,^,&,*,?,_,~]/)),
    };
    this._setPullTypes();
  }
  /**
   * Заполнить массив схемы пароля по типам символов в каждой строке
   */
  _setPullTypes() {
    if (this._charsByType.LOWER) {
      this._pullTypes.push(CharTypes.LOWER);
    }
    if (this._charsByType.UPPER) {
      this._pullTypes.push(CharTypes.UPPER);
    }
    if (this._charsByType.NUMBER) {
      this._pullTypes.push(CharTypes.NUMBER);
    }
    if (this._charsByType.SPECIAL) {
      this._pullTypes.push(CharTypes.SPECIAL);
    }
    if (this._charsByType.OTHER) {
      this._pullTypes.push(CharTypes.OTHER);
    }
    const pullTypesLength = this._setPullTypes.length;
    for (let i = pullTypesLength, n = 0; i < this._length; i++) {
      if (n === pullTypesLength) {
        n = 0;
      }
      this._pullTypes.push(this._pullTypes[n]);
      n++;
    }
  }
  /**
   * Получить символы по regExp выражению
   * @param regExp - регулярное выражение
   */
  _getChar(regExp) {
    const matches = this._chars.match(regExp);
    return matches ? matches.join('') : '';
  }
  /**
   * Сгенерировать пароль
   * @param lenth - длина генерируемого пароля
   */
  generate(length) {
    let pwd = '';
    length = length || this._length;
    let alphaCase = this.alphaCase || CharTypes.LOWER;
    for (let i = 0; i < length; i++) {
      const pullIndex = i % 10;
      let charType = this._pullTypes[pullIndex];
      if (charType === CharTypes.ALPHA) {
        if (this._charsByType.LOWER && this._charsByType.UPPER) {
          alphaCase = alphaCase === CharTypes.LOWER ? CharTypes.UPPER : CharTypes.LOWER;
          charType = alphaCase;
        }
      }
      const charString = this._charsByType[charType];
      pwd += charString.substr(Math.floor(Math.random() * charString.length), 1);
    }
    this.alphaCase = alphaCase;
    return pwd;
  }
}

const css = ".ispui-password{display:-ms-flexbox;display:flex;-ms-flex-flow:column;flex-flow:column;width:-webkit-fit-content;width:-moz-fit-content;width:fit-content}.ispui-password__inner{--pass-icon-color:var(--ispui-password-icon-color, var(--isp-icon-onField));display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;gap:var(--ispui-password-side-button-offset, 10px)}.ispui-password__eye-button{-ms-flex-order:1;order:1;margin:0;padding:0;cursor:pointer;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;border:none;background-color:transparent;font-size:0}.ispui-password__eye-button:hover,.ispui-password__eye-button:focus{opacity:0.8}.ispui-password__side-button{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}@supports not (gap: 10px){.ispui-password__side-button{margin-left:var(--ispui-password-side-button-offset, 10px)}}";

const Password = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.passwordGenerated = createEvent(this, "passwordGenerated", 7);
    this.changeEvent = createEvent(this, "customChange", 7);
    /** flag for eye icon visibility */
    this.eye = false;
    /** flag for generate button visibility */
    this.generate = false;
    /** input invalid indicator */
    this.invalid = false;
    /** input autocomplete flag */
    this.generateLabel = 'Генерировать';
    /** flag for copy button visibility */
    this.copy = false;
    /** label for copy button */
    this.copyLabel = 'Копировать';
    /** message for copy success action */
    this.copySuccessMessage = 'Скопировано!';
    /** flag for autogenerate password */
    this.isGeneratePassword = false;
    /** length for autogenerated password */
    this.passwordGenLength = 8;
    /** show placeholder if value empty */
    this.forceplaceholder = false;
    /** string with custom characters to generate password with */
    this.chars = '';
    /** show validation icon */
    this.validationIcon = false;
    this.isRequired = false;
    /** password field type */
    this.type = 'password';
    /** value */
    this.value = '';
    this.handleInputBind = this.handleInput.bind(this);
    this.observer = new MutationObserver(() => {
      this.isRequired = this.inputRef.required;
    });
  }
  get inputRef() {
    return this.host.querySelector('input');
  }
  updateGeneratorAndValue() {
    const options = {
      useNumber: true,
      useUpperCase: true,
      useLowerCase: true,
      useSpecial: false,
    };
    if (this.chars && typeof this.chars === 'string') {
      options.useNumber = false;
      options.useUpperCase = false;
      options.useLowerCase = false;
      options.customChars = this.chars;
    }
    this._genPasswd = new PasswordGenerator(this.passwordGenLength, options);
    if (this.isGeneratePassword) {
      this.generatePassword(this.passwordGenLength);
    }
  }
  /**
   * Generate password value
   * @param passwordLength - password length
   */
  async generatePassword(passwordLength) {
    this.type = 'text';
    this.value = this._genPasswd.generate(passwordLength);
    this.passwordGenerated.emit({ value: this.value });
    this.changeEvent.emit({ value: this.value });
    this.inputRef.dispatchEvent(new InputEvent('input', { bubbles: false, cancelable: true }));
  }
  setType(type) {
    this.inputRef.type = type;
  }
  setValue(value) {
    var _a;
    this.inputRef.value = value;
    (_a = this.inputWrapRef) === null || _a === void 0 ? void 0 : _a.validateInput();
  }
  /**
   * Password animation generation
   */
  async animatePasswordGeneration() {
    this.inputRef.type = 'text';
    this.type = 'text';
    let length = 0;
    this.value = this._genPasswd.generate(length);
    while (length < this.passwordGenLength) {
      await this.wait(35);
      length++;
      this.value = this._genPasswd.generate(length);
    }
    this.changeEvent.emit({ value: this.value });
    this.passwordGenerated.emit({ value: this.value });
    this.inputRef.dispatchEvent(new InputEvent('input', { bubbles: false, cancelable: true }));
  }
  /**
   * LIFECYCLE
   */
  componentWillLoad() {
    this.updateGeneratorAndValue();
    this.inputRef.addEventListener('input', this.handleInputBind);
    this.observer.observe(this.inputRef, {
      attributes: true,
    });
  }
  disconnectCallback() {
    this.inputRef.removeEventListener('input', this.handleInputBind);
    this.observer.disconnect();
  }
  /**
   * Toggle password field type
   */
  toggleValueVisibility() {
    this.type = this.type === 'password' ? 'text' : 'password';
  }
  /**
   * Form password class list
   */
  getHostClassList() {
    const classList = ['ispui-password'];
    return classList.join(' ');
  }
  wait(time) {
    return new Promise(resolve => {
      window.setTimeout(() => {
        resolve();
      }, time);
    });
  }
  /**
   * Copy current password value to clipboard
   * @param button - HTMLElement anchor for showing copy success message
   */
  async copyValue(button) {
    await clipboardCopy(this.value);
    showToast({
      toastContent: this.copySuccessMessage,
      anchorElement: button,
    });
  }
  /**
   * Sync input value && password value
   * @param e - input field event
   */
  handleInput(e) {
    this.value = e.target.value;
  }
  setCustomLabelClass() {
    const label = this.host.querySelector('label[slot="label"]');
    if (label) {
      label.classList.add('ispui-label');
      label.classList.toggle('ispui-label_required', this.isRequired);
    }
  }
  componentWillRender() {
    this.setCustomLabelClass();
  }
  render() {
    return (h(Host, { class: this.getHostClassList() }, !this.label && h("slot", { name: "label" }), h("div", { class: 'ispui-password__inner' }, h("ispui-input", { label: this.label, forceplaceholder: this.forceplaceholder, validationIcon: this.validationIcon, invalid: this.invalid, ref: ref => (this.inputWrapRef = ref) }, h("slot", null), this.eye && (h("button", { type: "button", slot: "icon", class: "ispui-password__eye-button", onClick: () => {
        this.toggleValueVisibility();
      } }, this.type === 'password' ? h(ClosedEyeIcon, null) : h(OpenEyeIcon, null))), h("span", { slot: "icon" }, h("slot", { name: "input-content" }))), this.generate && (h("ispui-link", { type: "default-hover", class: "ispui-password__side-button", onClick: () => this.animatePasswordGeneration() }, this.generateLabel)), this.copy && (h("ispui-link", { type: "default-hover", class: "ispui-password__side-button", onClick: e => this.copyValue(e.target) }, this.copyLabel)))));
  }
  get host() { return this; }
  static get watchers() { return {
    "isGeneratePassword": ["updateGeneratorAndValue"],
    "passwordGenLength": ["updateGeneratorAndValue"],
    "chars": ["updateGeneratorAndValue"],
    "type": ["setType"],
    "value": ["setValue"]
  }; }
  static get style() { return css; }
}, [4, "ispui-password", {
    "label": [1],
    "eye": [4],
    "generate": [4],
    "invalid": [516],
    "generateLabel": [1, "generate-label"],
    "copy": [4],
    "copyLabel": [1, "copy-label"],
    "copySuccessMessage": [1, "copy-success-message"],
    "isGeneratePassword": [4, "is-generate-password"],
    "passwordGenLength": [2, "password-gen-length"],
    "forceplaceholder": [516],
    "chars": [1],
    "validationIcon": [516, "validation-icon"],
    "isRequired": [32],
    "type": [32],
    "value": [32],
    "generatePassword": [64],
    "animatePasswordGeneration": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["ispui-password", "ispui-input", "ispui-link"];
  components.forEach(tagName => { switch (tagName) {
    case "ispui-password":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, Password);
      }
      break;
    case "ispui-input":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "ispui-link":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const IspuiPassword = Password;
const defineCustomElement = defineCustomElement$1;

export { IspuiPassword, defineCustomElement };
